import React from 'react';
import { useTranslation } from 'react-i18next';
import { SkillVariable } from '../types';

interface SkillVariableFormProps {
  variables: SkillVariable[];
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
}

/**
 * SkillVariableForm - used in Project settings page.
 * Renders a dynamic form based on the skill's variable declarations.
 * User fills in values that will be saved to the project.
 */
export default function SkillVariableForm({ variables, values, onChange }: SkillVariableFormProps) {
  const { t } = useTranslation('skills');
  if (!variables || variables.length === 0) return null;

  const updateValue = (key: string, val: string) => {
    onChange({ ...values, [key]: val });
  };

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 6, padding: 12, marginTop: 8,
      background: 'var(--surface-2)',
    }}>
      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8, color: 'var(--text)' }}>
        {t('variableForm.title')}
      </div>
      {variables.map(v => {
        const val = values[v.key] ?? v.default ?? '';
        return (
          <div key={v.key} style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>
              {v.label || v.key}
              {v.required && <span style={{ color: 'var(--danger)', marginLeft: 2 }}>*</span>}
            </label>
            {v.description && (
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>{v.description}</div>
            )}
            {v.type === 'textarea' ? (
              <textarea
                value={val}
                onChange={e => updateValue(v.key, e.target.value)}
                rows={3}
                style={inputStyle}
                placeholder={v.default || ''}
              />
            ) : v.type === 'select' ? (
              <select
                value={val}
                onChange={e => updateValue(v.key, e.target.value)}
                style={inputStyle}
              >
                <option value="">{t('variableForm.selectPlaceholder')}</option>
                {(v.options || []).map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ) : (
              <input
                value={val}
                onChange={e => updateValue(v.key, e.target.value)}
                style={inputStyle}
                placeholder={v.default || ''}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  display: 'block', width: '100%', padding: '6px 8px', fontSize: 13,
  border: '1px solid var(--border)', borderRadius: 4, marginTop: 2,
  boxSizing: 'border-box', background: 'var(--surface)', color: 'var(--text)',
};
